import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { Api } from '../../api/api';
import {
  getCurrentConsumptionPrice,
  getCurrentFeedInPrice,
  getEnergyPriceSeries,
  getExaaPriceSeries,
  parseEpexPriceSeries,
} from '../../api/functions';
import { ApiProblem } from '../../api/models/api-problem';
import { CurrentEnergyPriceResponse } from '../../api/models/current-energy-price-response';
import { EpexPriceResponse } from '../../api/models/epex-price-response';
import { ExaaPriceResponse } from '../../api/models/exaa-price-response';
import { PriceResponse } from '../../api/models/price-response';
import { LocationStateService } from '../../common/services/location-state.service';

/** Zeitzone, in der der gewählte Tag gilt. */
const LOCATION_TIME_ZONE = 'Europe/Vienna';

/** Ab dieser Abweichung (ct/kWh) zwischen zwei Quellen derselben Auktion wird die Zelle markiert. */
const DIVERGENCE_THRESHOLD_CT = 0.01;

/** Voller Zeitstempel inkl. UTC-Offset, damit Sommer-/Winterzeit beim Debuggen sichtbar ist. */
const TIMESTAMP_FORMAT = new Intl.DateTimeFormat('de-AT', {
  timeZone: LOCATION_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZoneName: 'shortOffset',
});

const OFFSET_PARTS_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: LOCATION_TIME_ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hourCycle: 'h23',
});

const DAY_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: LOCATION_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Offset von Europe/Vienna gegenüber UTC in Millisekunden zum gegebenen Zeitpunkt. */
function viennaOffsetMs(instant: Date): number {
  const parts = Object.fromEntries(
    OFFSET_PARTS_FORMAT.formatToParts(instant).map((part) => [part.type, Number(part.value)]),
  ) as Record<string, number>;
  const asUtc = Date.UTC(parts['year'], parts['month'] - 1, parts['day'], parts['hour'], parts['minute'], parts['second']);
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** Mitternacht (Europe/Vienna) des Tages yyyy-MM-dd, verschoben um {@code dayOffset} Tage. */
function viennaMidnight(date: string, dayOffset = 0): Date {
  const [year, month, day] = date.split('-').map(Number);
  const wallAsUtc = Date.UTC(year, month - 1, day + dayOffset);
  const firstGuess = wallAsUtc - viennaOffsetMs(new Date(wallAsUtc));
  // Zweiter Schritt korrigiert, falls der Offset an einer Sommer-/Winterzeitgrenze wechselt.
  return new Date(wallAsUtc - viennaOffsetMs(new Date(firstGuess)));
}

/** Heute (Europe/Vienna), verschoben um {@code dayOffset} Tage, als yyyy-MM-dd. */
function viennaDay(dayOffset = 0): string {
  const [year, month, day] = DAY_FORMAT.format(new Date()).split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + dayOffset)).toISOString().slice(0, 10);
}

/** Rechnet einen Preis anhand der Einheit der Quelle in ct/kWh um; unbekannte Einheiten bleiben unverändert. */
function toCtPerKwh(value: number | undefined, unit: string | undefined): number | undefined {
  if (value == null) {
    return undefined;
  }
  switch ((unit ?? '').replace(/\s+/g, '').toUpperCase()) {
    case 'EUR/MWH':
      return value / 10;
    case 'EUR/KWH':
      return value * 100;
    default:
      return value;
  }
}

/** Zahl mit 3 Nachkommastellen und Dezimalkomma; fehlende Werte bleiben leer. */
function csvNumber(value: number | undefined): string {
  return value == null ? '' : value.toFixed(3).replace('.', ',');
}

function csvCell(value: string): string {
  return /[";\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function toFileSafeName(value: string): string {
  return value.trim().replace(/[^\p{L}\p{N}_-]+/gu, '-').replace(/^-+|-+$/g, '') || 'standort';
}

/** Ladezustand einer einzelnen Quelle – jede Quelle lädt und scheitert unabhängig. */
interface SourceState<T> {
  loading: boolean;
  error: string | null;
  data: T | null;
}

const EMPTY: SourceState<never> = { loading: false, error: null, data: null };

interface PriceRow {
  time: number;
  entsoe?: number;
  apgExaa?: number;
  apgMc?: number;
  epex?: number;
}

@Component({
  selector: 'app-price-debugger',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
  ],
  templateUrl: './price-debugger.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./price-debugger.component.css'],
})
export class PriceDebuggerComponent {
  private readonly api = inject(Api);
  protected readonly locationState = inject(LocationStateService);

  readonly seriesPath = getEnergyPriceSeries.PATH;
  readonly exaaPath = getExaaPriceSeries.PATH;
  readonly epexPath = parseEpexPriceSeries.PATH;

  readonly selectedLocationId = computed(() => this.locationState.currentLocation()?.id ?? '');

  /** yyyy-MM-dd */
  readonly date = signal<string>(viennaDay());
  /** Welcher Schnellwahl-Tag (Heute/Morgen) dem gewählten Datum entspricht; null bei einem anderen Tag. */
  readonly dayPreset = computed<'today' | 'tomorrow' | null>(() => {
    const date = this.date();
    return date === viennaDay() ? 'today' : date === viennaDay(1) ? 'tomorrow' : null;
  });
  readonly from = computed(() => viennaMidnight(this.date()).toISOString());
  readonly to = computed(() => viennaMidnight(this.date(), 1).toISOString());

  readonly entsoe = signal<SourceState<PriceResponse>>(EMPTY);
  readonly apg = signal<SourceState<ExaaPriceResponse>>(EMPTY);
  readonly consumption = signal<SourceState<CurrentEnergyPriceResponse>>(EMPTY);
  readonly feedIn = signal<SourceState<CurrentEnergyPriceResponse>>(EMPTY);

  /** EPEX lässt keinen automatischen Abruf zu – die Tabelle wird im Browser kopiert und hier eingefügt. */
  readonly epexText = signal<string>('');
  readonly epex = signal<SourceState<EpexPriceResponse>>(EMPTY);
  /** Die eingefügte Tabelle gehört zu einem anderen Tag als dem gewählten. */
  readonly epexOtherDay = computed(() => {
    const deliveryDate = this.epex().data?.deliveryDate;
    return deliveryDate != null && deliveryDate !== this.date();
  });

  readonly loading = computed(() =>
    [this.entsoe, this.apg, this.consumption, this.feedIn, this.epex].some((source) => source().loading),
  );

  /** Alle Quellen nach Zeitstempel zusammengeführt, beschränkt auf den gewählten Tag. */
  readonly rows = computed<PriceRow[]>(() => {
    const start = new Date(this.from()).getTime();
    const end = new Date(this.to()).getTime();
    const byTime = new Map<number, PriceRow>();
    const row = (iso: string | undefined): PriceRow | undefined => {
      const time = iso ? new Date(iso).getTime() : NaN;
      if (Number.isNaN(time) || time < start || time >= end) {
        return undefined;
      }
      let existing = byTime.get(time);
      if (!existing) {
        existing = { time };
        byTime.set(time, existing);
      }
      return existing;
    };

    const entsoe = this.entsoe().data;
    for (const entry of entsoe?.data ?? []) {
      const target = row(entry.date);
      if (target) target.entsoe = toCtPerKwh(entry.value, entsoe?.unit);
    }
    const apg = this.apg().data;
    for (const entry of apg?.data ?? []) {
      const target = row(entry.timestamp);
      if (target) {
        target.apgExaa = toCtPerKwh(entry.exaaAuctionPrice, apg?.unit);
        target.apgMc = toCtPerKwh(entry.mcAuctionPrice, apg?.unit);
      }
    }
    const epex = this.epex().data;
    for (const entry of epex?.data ?? []) {
      const target = row(entry.timestamp);
      if (target) target.epex = toCtPerKwh(entry.price, epex?.unit);
    }
    return [...byTime.values()].sort((a, b) => a.time - b.time);
  });

  /** Nur das Ergebnis der letzten Anfrage zählt, falls Standort oder Tag schnell wechseln. */
  private requestCounter = 0;
  private epexRequestCounter = 0;

  constructor() {
    effect(() => {
      const locationId = this.selectedLocationId();
      const date = this.date();
      if (locationId && date) {
        this.load(locationId);
      }
    });
  }

  setLocationId(locationId: string): void {
    const next = this.locationState.locations().find((location) => location.id === locationId);
    if (next) {
      this.locationState.setLocation(next);
    }
  }

  shiftDay(days: number): void {
    const [year, month, day] = this.date().split('-').map(Number);
    const shifted = new Date(Date.UTC(year, month - 1, day + days));
    this.date.set(shifted.toISOString().slice(0, 10));
  }

  selectDay(preset: 'today' | 'tomorrow'): void {
    this.date.set(viennaDay(preset === 'tomorrow' ? 1 : 0));
  }

  reload(): void {
    const locationId = this.selectedLocationId();
    if (locationId) {
      this.load(locationId);
    }
  }

  /** Parst die eingefügte EPEX-Tabelle als Tabelle des gewählten Tages. */
  parseEpex(): void {
    const locationId = this.selectedLocationId();
    const text = this.epexText().trim();
    if (!locationId || !text) {
      return;
    }
    const request = ++this.epexRequestCounter;
    const date = this.date();
    this.epex.set({ loading: true, error: null, data: null });
    this.api.invoke(parseEpexPriceSeries, { location_id: locationId, date, body: text }).then(
      (data) => {
        if (request === this.epexRequestCounter) this.epex.set({ loading: false, error: null, data });
      },
      (error) => {
        if (request === this.epexRequestCounter) this.epex.set({ loading: false, error: this.formatError(error), data: null });
      },
    );
  }

  clearEpex(): void {
    this.epexRequestCounter++;
    this.epexText.set('');
    this.epex.set(EMPTY);
  }

  /** Lädt die Preistabelle als CSV herunter (Semikolon, Dezimalkomma – öffnet direkt in Excel/de). */
  exportCsv(): void {
    const rows = this.rows();
    if (rows.length === 0) {
      return;
    }
    const header = [
      'Zeit (Europe/Vienna)',
      'Zeit (UTC)',
      'ENTSO-E',
      'APG EXAA',
      'APG MC',
      'EPEX',
      'Δ ENTSO-E − APG MC',
      'Δ ENTSO-E − EPEX',
      'Δ APG MC − EPEX',
    ];
    const lines = rows.map((row) =>
      [
        this.timestamp(row.time),
        new Date(row.time).toISOString(),
        ...[
          row.entsoe,
          row.apgExaa,
          row.apgMc,
          row.epex,
          this.diff(row.entsoe, row.apgMc),
          this.diff(row.entsoe, row.epex),
          this.diff(row.apgMc, row.epex),
        ].map(csvNumber),
      ]
        .map(csvCell)
        .join(';'),
    );
    // BOM, damit Excel UTF-8 (Δ, Umlaute) korrekt erkennt
    const blob = new Blob(['\uFEFF' + [header.map(csvCell).join(';'), ...lines].join('\r\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const location = this.locationState.currentLocation()?.name ?? 'standort';
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `preise_${toFileSafeName(location)}_${this.date()}_export_${DAY_FORMAT.format(new Date())}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  timestamp(value: number | string | undefined): string {
    return value == null ? '–' : TIMESTAMP_FORMAT.format(new Date(value));
  }

  diff(a: number | undefined, b: number | undefined): number | undefined {
    return a == null || b == null ? undefined : a - b;
  }

  divergent(a: number | undefined, b: number | undefined): boolean {
    const delta = this.diff(a, b);
    return delta != null && Math.abs(delta) > DIVERGENCE_THRESHOLD_CT;
  }

  private load(locationId: string): void {
    const request = ++this.requestCounter;
    const from = this.from();
    const to = this.to();
    this.fetch(request, this.entsoe, () =>
      this.api.invoke(getEnergyPriceSeries, { location_id: locationId, source: 'transparency-energy', resolution: '15m', from, to }),
    );
    this.fetch(request, this.apg, () => this.api.invoke(getExaaPriceSeries, { location_id: locationId, from, to }));
    this.fetch(request, this.consumption, () => this.api.invoke(getCurrentConsumptionPrice, { location_id: locationId }));
    this.fetch(request, this.feedIn, () => this.api.invoke(getCurrentFeedInPrice, { location_id: locationId }));
  }

  private async fetch<T>(request: number, target: WritableSignal<SourceState<T>>, call: () => Promise<T>): Promise<void> {
    target.set({ loading: true, error: null, data: null });
    try {
      const data = await call();
      if (request === this.requestCounter) {
        target.set({ loading: false, error: null, data });
      }
    } catch (error) {
      if (request === this.requestCounter) {
        target.set({ loading: false, error: this.formatError(error), data: null });
      }
    }
  }

  private formatError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return 'Die API ist nicht erreichbar.';
      }
      const problem = (error.error && typeof error.error === 'object' ? error.error : undefined) as ApiProblem | undefined;
      const detail = [problem?.title, problem?.detail].filter(Boolean).join(' — ');
      return detail ? `${error.status}: ${detail}` : `${error.status} ${error.statusText}`;
    }
    return error instanceof Error ? error.message : String(error);
  }
}
